import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FxRateService } from '@/services/FxRateService';

// Query keys
export const fxRateKeys = {
  all: ['fxRates'] as const,
  rate: (from: string, to: string, date?: string) => 
    [...fxRateKeys.all, 'rate', from, to, date] as const,
  history: (from: string, to: string, start: string, end: string) =>
    [...fxRateKeys.all, 'history', from, to, start, end] as const,
};

/**
 * Hook for fetching a single FX rate.
 */
export function useFxRate(from: string, to: string, date?: string) {
  return useQuery({
    queryKey: fxRateKeys.rate(from, to, date),
    queryFn: () => FxRateService.getRate(from, to, date),
    enabled: !!from && !!to && from !== to,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook for fetching FX rate history.
 */
export function useFxRateHistory(
  from: string,
  to: string,
  startDate: string,
  endDate: string
) {
  return useQuery({
    queryKey: fxRateKeys.history(from, to, startDate, endDate),
    queryFn: () => FxRateService.getHistory(from, to, startDate, endDate),
    enabled: !!from && !!to && !!startDate && !!endDate,
    staleTime: 60 * 60 * 1000,
  });
}

/**
 * Hook for currency conversion.
 */
export function useConvertCurrency() {
  return useMutation({
    mutationFn: ({ 
      amount, 
      from, 
      to, 
      date 
    }: { 
      amount: number; 
      from: string; 
      to: string; 
      date?: string;
    }) => FxRateService.convert(amount, from, to, date),
  });
}

/**
 * Hook for quick currency conversion with caching.
 */
export function useCachedConversion(
  amount: number,
  from: string,
  to: string,
  date?: string
) {
  const { data: rate } = useFxRate(from, to, date);

  if (from === to || !amount) {
    return {
      convertedAmount: amount,
      rate: 1,
      isLoading: false,
    };
  }

  return {
    convertedAmount: rate ? amount * rate : null,
    rate,
    isLoading: !rate,
  };
}

/**
 * Hook for FX rate utilities.
 */
export function useFxRateUtils() {
  return {
    formatAmount: FxRateService.formatAmount,
    formatRate: FxRateService.formatRate,
    getCommonCurrencies: FxRateService.getCommonCurrencies,
    getCurrencySymbol: FxRateService.getCurrencySymbol,
  };
}

/**
 * Hook for currency formatting.
 */
export function useCurrencyFormatter(currency: string, locale = 'en-US') {
  return {
    format: (amount: number) => FxRateService.formatAmount(amount, currency, locale),
    symbol: FxRateService.getCurrencySymbol(currency),
    currency,
  };
}

/**
 * Hook for rate cache management.
 */
export function useFxRateCache() {
  const queryClient = useQueryClient();

  const refreshRates = async (currencies: string[]) => {
    await FxRateService.refreshCache(currencies);
    queryClient.invalidateQueries({ queryKey: fxRateKeys.all });
  };

  const isStale = async () => FxRateService.isRateCacheStale();

  return { refreshRates, isStale };
}
